import classNames from 'classnames'
import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { UnsubscribeAttentionModal } from '@pages/Subscriptions/modals/UnsubscribeAttention.modal'
import { UnsubscribeConfirmationModal } from '@pages/Subscriptions/modals/UnsubscribeConfirmation.modal'

import { useModal } from '@context/ModalsContext'

import { Body1, Body2, Caption1, Heading6 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Button, IconButton } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { TrashIcon } from '@ui/icons/Trash.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { isLastBusinessDay } from '@utils/date.utils'
import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path'

import { NameDto } from '@model/NameDto'
import { SubscriptionCartItem } from '@model/SubscriptionCart'
import { SubscriptionRequestState } from '@model/SubscriptionRequestState'
import { SubscriptionRequestTypeEnum } from '@model/SubscriptionRequestTypeEnum'
import {
  SubscriptionGroupType,
  SubscriptionHistoryItem,
  SubscriptionStatus,
  isSubscriptionAllowedToDelete,
} from '@model/SubscriptionsDto'
import {
  SubscriptionRequestType,
  SubscriptionTimeInForce,
  UpdateSubscriptionRequest,
} from '@model/UpdateSubscriptionRequest'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { InformationModal } from '@shared/modals/Information.modal'

import { PendingUnsubscriptionModal } from './MarketData/modals/PendingUnsubscription.modal'
import { InsufficientFundsModal, UnableToCheckoutModal } from './modals/CheckoutMessage.modal'

import styles from './Cart.module.scss'

interface SubscriptionItemProps {
  name: string | undefined
  state?: NameDto
  amount: number | undefined
  onClickTrash: () => void
  isPendingUnsubscriptionRequest?: boolean
  onClickPendingSubscriptionInfoModal?(): void
  isPendingImmediateSubscription?: boolean
}

const SubscriptionItem: FC<SubscriptionItemProps> = ({
  name,
  state,
  amount,
  onClickTrash,
  isPendingUnsubscriptionRequest,
  onClickPendingSubscriptionInfoModal,
  isPendingImmediateSubscription,
}) => {
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()

  const showTrashIcon = useMemo(
    () =>
      !isPendingUnsubscriptionRequest &&
      !isPendingImmediateSubscription &&
      isSubscriptionAllowedToDelete(state),
    [isPendingUnsubscriptionRequest, isPendingImmediateSubscription, state]
  )

  return (
    <div className='is-flex is-justify-content-space-between mb-2'>
      <div className='is-flex'>
        <Box flex direction='column'>
          <Body2>{name}</Body2>
          {isPendingUnsubscriptionRequest && (
            <Box flex align='center' gap='x4'>
              <Caption1 isParagraph color='text-warning-high-em'>
                ({t('Subscriptions.Pending Unsubscription')})
              </Caption1>
              <ActionIcon
                icon={InfoIcon}
                size='x20'
                onClick={onClickPendingSubscriptionInfoModal}
              />
            </Box>
          )}
        </Box>
      </div>
      <div className='is-flex is-align-items-center'>
        <Body2>{formatMoney(amount)}</Body2>
        {showTrashIcon && (
          <span className='ml-2 is-clickable' onClick={onClickTrash}>
            <TrashIcon size={16} />
          </span>
        )}
      </div>
    </div>
  )
}

interface CartProps {
  cartItemsIsLoading: boolean
  handleOnUpdateSubscription(
    data: UpdateSubscriptionRequest,
    selectedActiveSubscriptionCartItem?: SubscriptionHistoryItem | undefined
  ): void
  handleOnClickPay(): void
  onRemoveCartItem(id: string): void
  isExpanded?: boolean
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>
  hasPendingUnsubscriptionRequest?: boolean
  activeSubscriptions?: SubscriptionHistoryItem[]
  noneActiveSubscriptions?: SubscriptionCartItem[]
  pendingUnsubscriptionsData?: SubscriptionHistoryItem[]
  availableToWithdraw?: number
  hasSubscriptions: boolean
  upgradeOrDowngradeSubscriptionId: Set<string>
}

export const Cart: React.FC<CartProps> = ({
  handleOnClickPay,
  handleOnUpdateSubscription,
  onRemoveCartItem,
  activeSubscriptions = [],
  noneActiveSubscriptions = [],
  cartItemsIsLoading,
  hasPendingUnsubscriptionRequest,
  isExpanded,
  setIsExpanded,
  pendingUnsubscriptionsData,
  availableToWithdraw = 0,
  hasSubscriptions,
  upgradeOrDowngradeSubscriptionId,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const { formatMoney } = useFormatNumber()
  const { scrollIntoView } = useScrollToElementIds()

  const pendingUnsubscriptionModal = useModal(PendingUnsubscriptionModal)
  const unableToCheckoutModal = useModal(UnableToCheckoutModal)
  const cancelOrderModal = useModal(InformationModal, {
    title: t('Subscriptions.Cancel Order?'),
    secondaryLabel: t('Subscriptions.Keep Order'),
    primaryLabel: t('Subscriptions.Yes, cancel'),
  })

  const attentionModal = useModal(UnsubscribeAttentionModal)
  const confirmationModal = useModal(UnsubscribeConfirmationModal)

  const isPendingUnsubscriptionRequest = (subscription: SubscriptionHistoryItem) => {
    return pendingUnsubscriptionsData?.some(
      (item) =>
        item?.subscriptionDetailId === subscription?.subscriptionDetailId &&
        item?.state?.id === subscription?.state?.id &&
        item?.type?.id === subscription?.type?.id
    )
  }

  const isPendingImmediateSubscription = (subscription: SubscriptionHistoryItem) => {
    return (
      subscription.subscriptionTimeInForce.id === SubscriptionTimeInForce.Immediate &&
      subscription.state.id === SubscriptionStatus.PENDING
    )
  }

  const filterByNonBillableSubscription = (
    data: SubscriptionHistoryItem[] = []
  ): SubscriptionHistoryItem[] =>
    data?.filter(
      (item: SubscriptionHistoryItem) =>
        !(
          item.state.name === SubscriptionRequestState.Active &&
          upgradeOrDowngradeSubscriptionId.has(item.subscription.id)
        ) &&
        !(
          item.state.name === SubscriptionRequestState.Pending &&
          item.type.id === SubscriptionRequestType.Cancel
        )
    )

  const getUpgradeDifference = (cartItem: SubscriptionCartItem): number => {
    const levelToUpgrade = activeSubscriptions.find(
      (activeItem) => activeItem.subscription.id === cartItem.subscription.id
    )
    return levelToUpgrade?.amount
      ? cartItem.subscriptionDetail.amount - levelToUpgrade.amount
      : cartItem.subscriptionDetail.amount
  }

  const sumOfNoneActiveSubscriptions = (data?: SubscriptionCartItem[]): number => {
    return (
      data?.reduce((acc: number, noneActiveSubscription: SubscriptionCartItem) => {
        if (
          noneActiveSubscription.subscriptionRequestType.name ===
          SubscriptionRequestTypeEnum.UpdateExistingSubscription
        ) {
          return acc + getUpgradeDifference(noneActiveSubscription)
        }

        return acc + (noneActiveSubscription.subscriptionDetail?.amount || 0)
      }, 0) || 0
    )
  }

  const sumOfActiveSubscriptions = (data?: SubscriptionHistoryItem[]) =>
    filterByNonBillableSubscription(data).reduce(
      (acc: number, activeSubscription: SubscriptionHistoryItem) =>
        acc + (activeSubscription?.amount || 0),
      0
    ) || 0

  const billableNextMonth: number = sumOfActiveSubscriptions(activeSubscriptions)
  const totalToPayNow: number = sumOfNoneActiveSubscriptions(noneActiveSubscriptions)
  const total = totalToPayNow + billableNextMonth

  const insufficientFundsModal = useModal(InsufficientFundsModal)
  const hasNotEnoughFunds = availableToWithdraw < totalToPayNow
  const hasNotEnoughFundsForCardBillable = availableToWithdraw < billableNextMonth

  const isCheckoutDisabled = useMemo(() => {
    return !hasSubscriptions || !noneActiveSubscriptions.length || hasNotEnoughFunds
  }, [hasSubscriptions, noneActiveSubscriptions, hasNotEnoughFunds])

  const isPaymentAllowed = useMemo(() => {
    return !!noneActiveSubscriptions.length
  }, [noneActiveSubscriptions])

  const handleOnClickPendingUnsubscriptionModal = (
    activeSubscriptionCartItem: SubscriptionHistoryItem
  ) => {
    pendingUnsubscriptionModal.open({ dueDate: activeSubscriptionCartItem?.dueDate })
  }

  const handleOnUnsubscribe = (item: SubscriptionHistoryItem) => {
    handleOnUpdateSubscription(
      {
        subscriptions: [
          {
            subscriptionDetailId: item.subscriptionDetailId,
            subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
          },
        ],
        subscriptionTimeInForceId: SubscriptionTimeInForce.BeginningOfNextMonth,
      },
      item
    )

    attentionModal.close()
    confirmationModal.close()
  }

  const handleOnCancelPendingSubscription = (item: SubscriptionHistoryItem) => {
    cancelOrderModal.close()
    handleOnUpdateSubscription({
      subscriptions: [
        {
          subscriptionDetailId: item.subscriptionDetailId,
          subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
        },
      ],
      subscriptionTimeInForceId: SubscriptionTimeInForce.Immediate,
    })
  }

  const handleOnClickTrash = (activeSubscription: SubscriptionHistoryItem) => {
    if (activeSubscription.state.name === SubscriptionRequestState.Pending) {
      cancelOrderModal.open({
        onPrimaryClick: () => handleOnCancelPendingSubscription(activeSubscription),
      })
    } else if (isLastBusinessDay()) {
      attentionModal.open({
        onConfirm: () => handleOnUnsubscribe(activeSubscription),
      })
    } else {
      confirmationModal.open({
        onConfirm: () => handleOnUnsubscribe(activeSubscription),
      })
    }
  }

  return (
    <Loading showLoadingIcon isLoading={cartItemsIsLoading}>
      <Box flex justify='space-between' align='center'>
        <Box flex align='center' gap='x8'>
          {(hasNotEnoughFunds || hasNotEnoughFundsForCardBillable) && isMobile && (
            <Box
              onClick={() =>
                insufficientFundsModal.open({
                  navigationUrl: `${CommonPaths.Deposit}/wallet`,
                })
              }
            >
              <WarningIcon color='text-danger-high-em' />
            </Box>
          )}
          <Heading6 medium>{t('Subscriptions.Cart')}</Heading6>
        </Box>
        <Box flex gap='x8' align='center'>
          {hasNotEnoughFunds && !isMobile && (
            <Box
              onClick={() =>
                insufficientFundsModal.open({
                  navigationUrl: `${CommonPaths.Deposit}/walelt`,
                })
              }
            >
              <WarningIcon color='text-danger-high-em' />
            </Box>
          )}

          {hasNotEnoughFundsForCardBillable && !isMobile && (
            <Box
              onClick={() =>
                insufficientFundsModal.open({
                  navigationUrl: `${CommonPaths.TradersRoomWallets}/transfer`,
                })
              }
            >
              <WarningIcon color='text-danger-high-em' />
            </Box>
          )}

          {!isExpanded && isMobile && <Heading6 medium>{formatMoney(total)}</Heading6>}

          {isMobile && (
            <IconButton.Ghost
              icon={isExpanded ? ChevronIcon.Up : ChevronIcon.Down}
              onClick={() => {
                if (!isExpanded) {
                  scrollIntoView([ScrollToIds.SubscriptionsSidebarWrapper])
                }
                setIsExpanded(!isExpanded)
              }}
            />
          )}
        </Box>
      </Box>
      <div className={classNames(styles.expandedContent, { [styles.expanded]: isExpanded })}>
        {hasPendingUnsubscriptionRequest && (
          <>
            <div className='mt-4 text has-text-warning'>
              <Body1 className='mr-2' medium>
                {t('Subscriptions.ATTENTION')}!
              </Body1>
              <span>{t('Subscriptions.You have pending requests outstanding')}</span>
            </div>
            <div className='mt-2 text text-secondary'>
              {t('Subscriptions.When your request has been accepted')}
            </div>
          </>
        )}
        {hasNotEnoughFundsForCardBillable && (
          <>
            <div className='mt-4 text has-text-danger'>
              <Body1 className='mr-2' medium>
                {t('Subscriptions.ATTENTION')}!
              </Body1>
              <span>{t('Subscriptions.You might have insufficient funds')}</span>
            </div>
            <div className='mt-2 text text-secondary'>
              {t('Subscriptions.After depositing funds to your wallet')}
            </div>
            <div className='my-4'>
              <Button.Primary
                size='md'
                fullWidth
                onClick={() => navigate(`${CommonPaths.TradersRoomWallets}/transfer`)}
              >
                {t('Subscriptions.Fund Tickmill Trading Account')}
              </Button.Primary>
            </div>
          </>
        )}
        <div className='is-flex is-justify-content-space-between my-4'>
          <Caption1 className='text-secondary'>{t('Market Data.Product')}</Caption1>
          <Caption1 className='text-secondary'>{t('Market Data.Price')}</Caption1>
        </div>
        {activeSubscriptions?.map((activeSubscription) => (
          <SubscriptionItem
            key={activeSubscription.id}
            name={`${activeSubscription?.subscription?.name} ${
              activeSubscription?.subscriptionGroupType?.name ===
                SubscriptionGroupType.MARKET_DATA && activeSubscription?.name
                ? `(${activeSubscription?.name})`
                : ''
            }`}
            state={activeSubscription.state}
            amount={activeSubscription.amount}
            isPendingUnsubscriptionRequest={isPendingUnsubscriptionRequest(activeSubscription)}
            isPendingImmediateSubscription={isPendingImmediateSubscription(activeSubscription)}
            onClickTrash={() => handleOnClickTrash(activeSubscription)}
            onClickPendingSubscriptionInfoModal={() =>
              handleOnClickPendingUnsubscriptionModal(activeSubscription)
            }
          />
        ))}
        {noneActiveSubscriptions?.map((noneActiveSubscription) => (
          <SubscriptionItem
            key={noneActiveSubscription.id}
            name={`${noneActiveSubscription?.subscription?.name} ${
              noneActiveSubscription?.subscription?.subscriptionGroupType?.name ===
                SubscriptionGroupType.MARKET_DATA &&
              noneActiveSubscription?.subscriptionDetail?.name
                ? `(${noneActiveSubscription.subscriptionDetail?.name})`
                : ''
            }`}
            amount={noneActiveSubscription.subscriptionDetail.amount}
            onClickTrash={() => {
              cancelOrderModal.open({
                onPrimaryClick: () => {
                  cancelOrderModal.close()
                  onRemoveCartItem(noneActiveSubscription.id)
                },
              })
            }}
          />
        ))}
        <div className='is-flex is-justify-content-space-between'>
          <div>
            <Body2 medium>{t('Market Data.Pay Now')}</Body2>
          </div>
          <Body2 medium>{formatMoney(totalToPayNow)}</Body2>
        </div>
        <div className='is-flex is-justify-content-space-between my-3'>
          <div>
            <Body2 medium>{t('Market Data.Monthly recurring payment')}</Body2>
          </div>
          <Body2 medium>{formatMoney(billableNextMonth)}</Body2>
        </div>
        <div className='is-flex is-justify-content-space-between my-3'>
          <div>
            <Body2 medium>{t('Market Data.Total')}</Body2>
          </div>
          <Body2 medium>{formatMoney(total)}</Body2>
        </div>
        {isPaymentAllowed && (
          <div className='is-flex is-flex-direction-column is-justify-content-center'>
            <div className='my-4'>
              <Button.Primary
                size='md'
                fullWidth
                disabled={isCheckoutDisabled}
                onClick={handleOnClickPay}
              >{`${t('Market Data.Pay Now')} ${formatMoney(totalToPayNow)} `}</Button.Primary>
            </div>
            {isCheckoutDisabled && (
              <Link size='md' fullWidth onClick={() => unableToCheckoutModal.open()}>
                {t('Subscriptions.Why I can’t checkout?')}
              </Link>
            )}
          </div>
        )}
      </div>
    </Loading>
  )
}
